// TaskQueueProgress is a data object that provides insight into the Task
// Queue's progress toward completing the tasks that have been enqueued since
// the last time the TaskQueue was empty. When the TaskQueue is empty, the
// progress is reset when the next task enqueued; i.e., enqueuedCount will be
// 1.
export interface TaskQueueProgress {
    // The number of tasks that have finished running since the last time the
    // TaskQueue was empty.
    readonly completedCount: number,

    // The number of tasks that have been enqueued (regardless of whether they
    // have finished running) since the last time the TaskQueue was empty.
    readonly enqueuedCount: number,

    // A value between 0.0 and 1.0 indicating how many of the enqueued tasks
    // have been completed.
    readonly progress: number,

    // The number of unfinished tasks that remain in the TaskQueue.
    readonly remainingCount: number,
}

// TaskQueueProgressTracker is used by TaskQueue to track the progress of the
// tasks processed by the TaskQueue.
class TaskQueueProgressTracker implements TaskQueueProgress {
    private _enqueued = 0;
    private _completed = 0;

    // Implement TaskQueueProgress
    public get completedCount() {
        return this._completed;
    }

    // Implement TaskQueueProgress
    public get enqueuedCount() {
        return this._enqueued;
    }

    // Implement TaskQueueProgress
    public get progress() {
        let p = 0.0;
        if (this._enqueued > 0) {
            p = this._completed / this._enqueued;
        }
        return p;
    }

    // Implement TaskQueueProgress
    public get remainingCount() {
        return (this._enqueued - this._completed);
    }

    // Reset the progress to no (0) enqueued or completed tasks.
    public reset() {
        this._completed = 0;
        this._enqueued = 0;
    }

    // Update the object to note that a task has been completed.
    public taskCompleted() {
        ++this._completed;
        if (this._completed > this._enqueued) {
            this._completed = this._enqueued;
        }
    }

    // Update the object to note that a task has been enqueued.
    public taskEnqueued() {
        ++this._enqueued;
    }
}

// TaskQueue asynchronously executes tasks in a manner intended to keep the
// browser responsive to user interaction. A "task" is a function that takes no
// arguments and returns nothing (void).
//
// Since multiple TaskQueue instances do not coordinate their work, it is
// recommended that the global TaskQueue.shared instance always be used, rather
// than instantiating separate TaskQueue instances.
export class TaskQueue {

    ////////////////////////////////////////////////////////////////////////////
    // STATIC

    // PROPERTIES

    private static _shared = new TaskQueue();

    // Global TaskQueue
    public static get shared() {
        return TaskQueue._shared;
    }

    // PRIVATE METHODS

    // Creates a processor function to run the tasks remaining on the TaskQueue
    // and schedules it for execution at the next available opportunity.
    private static _scheduleProcessing(tq: TaskQueue) {
        const processor = TaskQueue._taskProcessor(tq);
        window.requestAnimationFrame(processor);
        tq._scheduled = true;
    }

    // Creates a function to be run with Window.requestAnimationFrame. The
    // function will process as many tasks as it can in ~3ms; if there are more
    // tasks remaining, the function schedules additional processing.
    private static _taskProcessor(tq: TaskQueue): (number) => void {
        return function _processTasks(startTime: number) {
            tq._scheduled = false;
            if (tq._queue.length < 1) {
                return;
            }

            let canProcessNextTask = (tq._queue.length > 0);
            while (canProcessNextTask) {
                // Take the oldest task off the head of the queue
                const task = tq._queue.shift();

                // Run the task
                try {
                    task();
                } catch (e) {
                    window.console.error(e);
                } finally {
                    tq._currentProgress.taskCompleted();
                }

                // Go again if there is enough time to do the next task
                const now = window.performance.now();
                const elapsedTimeMS = now - startTime;
                canProcessNextTask = (tq._queue.length > 0) && (elapsedTimeMS < 3.0);
            }

            if (tq._queue.length > 0) {
                TaskQueue._scheduleProcessing(tq);
            }
        }
    }

    ////////////////////////////////////////////////////////////////////////////
    // INSTANCE

    // PROPERTIES

    private _currentProgress = new TaskQueueProgressTracker();
    private _queue: Array<() => void> = [];
    private _scheduled = false;

    // currentProgress holds the progress object for this TaskQueue. The same
    // TaskQueueProgress instance is used throughout the life of this
    // TaskQueue, so components that only need to know the status of this
    // TaskQueue can hold a reference to the TaskQueueProgress rather than
    // holding a reference to an entire TaskQueue.
    public get currentProgress(): TaskQueueProgress {
        return this._currentProgress;
    }

    // isIdle is true when all enqueued tasks have been processed; false
    // otherwise. Stated differently, isIdle is true when there are no tasks
    // currently processing or scheduled to be processed. This property is the
    // opposite of isProcessing.
    public get isIdle(): boolean {
        return !this.isProcessing;
    }

    // isProcessing is true when there are any tasks that have been enqueued
    // but have not yet been completed; false otherwise. This property is the
    // opposite of isIdle.
    public get isProcessing(): boolean {
        return (this._queue.length > 0);
    }

    // PUBLIC METHODS

    // Add a task to the queue to be processed.
    public enqueue(task: () => void) {
        if (typeof task !== 'function') {
            return;
        }

        if (this._queue.length < 1) {
            // Start a new batch of tasks
            this._currentProgress.reset();
            TaskQueue._scheduleProcessing(this);
        }

        // Add the new task to the tail of the queue
        this._queue.push(task);
        // Update the progress tracker
        this._currentProgress.taskEnqueued();
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/common/taskQueue.ts